import { fourWireMotorType } from "constant";
import { ActuatorCommandMode } from "types/Actuator";

export type ControllerStatusDisplayType = "onoff" | "value";

export type ControllerStatusInfo = {
    status: string;
    statusDisplayType: ControllerStatusDisplayType;
};

const parseFiniteNumber = (raw: any): number | null => {
    if (typeof raw === "number" && Number.isFinite(raw)) {
        return raw;
    }
    if (typeof raw === "string" && raw.trim() !== "") {
        const n = Number(raw);
        if (Number.isFinite(n)) {
            return n;
        }
    }
    return null;
};

/**
 * Registry `CMDS[said].mode` (LEVEL = dimmer/color, MOTOR = 4-wire motor).
 * If CMDS is empty, infer from type label (sensor_names / APP NAME).
 */
const getEffectiveActuatorMode = (actuator: any): string | undefined => {
    const fromCmd = actuator?.info?.mode;
    if (fromCmd && String(fromCmd).trim() !== "") {
        return String(fromCmd).toUpperCase();
    }
    const t = String(actuator?.type ?? "");
    if (t === fourWireMotorType || /\bmotor\b/i.test(t)) {
        return ActuatorCommandMode.MOTOR;
    }
    if (/\b(brightness|color)\b/i.test(t)) {
        return ActuatorCommandMode.LEVEL;
    }
    return undefined;
};

/** LEVEL: non-zero reading ⇒ on (dim level, packed RGB, etc.). */
const levelReadingToOnOff = (raw: any): ControllerStatusInfo | null => {
    if (raw === false) return { status: "Off", statusDisplayType: "onoff" };
    if (raw === true) return { status: "On", statusDisplayType: "onoff" };
    const n = parseFiniteNumber(raw);
    if (n === null) return null;
    if (n > 0) return { status: "On", statusDisplayType: "onoff" };
    return { status: "Off", statusDisplayType: "onoff" };
};

/** MOTOR: registry CONTROL [-1, 0, 1]; 0 = stopped, ±1 = active. */
const motorReadingToOnOff = (raw: any): ControllerStatusInfo | null => {
    const n = parseFiniteNumber(raw);
    if (n === null) return null;
    if (n === 0) return { status: "Off", statusDisplayType: "onoff" };
    if (n === 1 || n === -1) return { status: "On", statusDisplayType: "onoff" };
    return null;
};

/** Prefer readings.status, then readings.value, then legacy top-level status. */
const getRawControllerReading = (actuator: any): any => {
    const r = actuator?.readings;
    if (r) {
        if (r.status !== undefined && r.status !== null && r.status !== "") {
            return r.status;
        }
        if (r.value !== undefined && r.value !== null && r.value !== "") {
            return r.value;
        }
    }
    if (actuator?.status !== undefined && actuator?.status !== null && actuator?.status !== "") {
        return actuator.status;
    }
    return null;
};

const classifyBinaryOnOff = (raw: any): "off" | "on" | null => {
    if (typeof raw === "boolean") {
        return raw ? "on" : "off";
    }

    if (typeof raw === "string") {
        const normalized = raw.trim().toLowerCase();
        if (normalized === "off" || normalized === "false") return "off";
        if (normalized === "on" || normalized === "true") return "on";
    }

    const numeric = parseFiniteNumber(raw);
    if (numeric === 0) return "off";
    if (numeric === 1) return "on";
    return null;
};

const isPlainNumericReading = (raw: any): boolean => parseFiniteNumber(raw) !== null;

export const getControllerStatusInfo = (actuator: any): ControllerStatusInfo => {
    const raw = getRawControllerReading(actuator);
    const mode = getEffectiveActuatorMode(actuator);

    if (mode === ActuatorCommandMode.LEVEL) {
        const level = levelReadingToOnOff(raw);
        if (level) return level;
    } else if (mode === ActuatorCommandMode.MOTOR) {
        const motor = motorReadingToOnOff(raw);
        if (motor) return motor;
    }

    const binary = classifyBinaryOnOff(raw);
    if (binary === "off") {
        return { status: "Off", statusDisplayType: "onoff" };
    }
    if (binary === "on") {
        return { status: "On", statusDisplayType: "onoff" };
    }

    if (raw != null && raw !== "" && typeof raw !== "object") {
        const display = String(raw);
        return {
            status: display,
            statusDisplayType: isPlainNumericReading(raw) ? "value" : "onoff",
        };
    }

    return {
        status: "-",
        statusDisplayType: "onoff",
    };
};

/** Sort controllers tab consistently with displayed On/Off. */
export const getControllerStatusSortValue = (actuator: any): number => {
    const { status } = getControllerStatusInfo(actuator);
    if (status === "On") return 1;
    if (status === "Off") return 0;
    return 0;
};
